import { SchemaItem, StructureColumnTypeEnum, StructureItemType } from "./Models/Models";
import { ModelType } from "./Models/ModelTypes";

const baseDefaults = {
  DatasetFilter: null,
  MetricsDatasetFilter: null,
  ExcludeSchemaItems: [] as string[]
};

function build(modelDefaults: Record<string, unknown>) {
  return { ...modelDefaults, ...baseDefaults };
}

function getSchemaItemLength(item: SchemaItem): number {
  switch (item.valueTypeId) {
    case StructureItemType.Number:
      return 1;
    case StructureItemType.NumberArray:
      return item.arrayLength ?? 1;
    case StructureItemType.Category:
      if ((item.categories?.length ?? 0) === 2) {
        return 1;
      }
      return item.categories?.length ?? 1;
    case StructureItemType.String:
      return 16;
    default:
      return 1;
  }
}

function getInputsOutputsLength(schemaItems: SchemaItem[]): { inputsCount: number; outputsCount: number } {
  if (!Array.isArray(schemaItems)) return { inputsCount: 0, outputsCount: 0 };
  
  const inputs = schemaItems.filter((s) => s.typeId === StructureColumnTypeEnum.Feature);
  const outputs = schemaItems.filter((s) => s.typeId === StructureColumnTypeEnum.Target);
  
  const inputsCount = inputs.reduce((sum, item) => sum + getSchemaItemLength(item), 0);
  const outputsCount = outputs.reduce((sum, item) => sum + getSchemaItemLength(item), 0);
  
  return { inputsCount, outputsCount };
}

function getFeatureOrdering(schemaItems: SchemaItem[]): string[] {
  if (!Array.isArray(schemaItems)) return [];
  return schemaItems
    .filter((s) => s.typeId === StructureColumnTypeEnum.Feature)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((s) => s.name);
}

function needsEmbedding(schemaItems: SchemaItem[]): boolean {
  if (!Array.isArray(schemaItems)) return false;
  const inputs = schemaItems.filter((s) => s.typeId === StructureColumnTypeEnum.Feature);
  return inputs.some((s) =>
    s.valueTypeId === StructureItemType.NumberArray ||
    s.valueTypeId === StructureItemType.String ||
    (s.valueTypeId === StructureItemType.Category && (s.categories?.length ?? 0) > 2)
  );
}

function getDefaultSequential(schemaItems: SchemaItem[]): Array<{ ActivationKey: string; Value: number; Type: string; }> {
  const { inputsCount, outputsCount } = getInputsOutputsLength(schemaItems);
  return [
    {
      ActivationKey: "Relu",
      Value: inputsCount,
      Type: "Dense",
    },
    {
      ActivationKey: outputsCount > 1 ? "Softmax" : "Sigmoid",
      Value: outputsCount,
      Type: "Dense",
    },
  ];
}

function getDefaultMetrics(schemaItems: SchemaItem[]): string[] {
  const { outputsCount } = getInputsOutputsLength(schemaItems);
  return outputsCount > 1 ? ["CategoricalAccuracy"] : ["BinaryAccuracy"];
}

export function getDefaultModelSettings(
  modelTypeId: ModelType,
  schemaItems: SchemaItem[] = []
): string {
    let settings: Record<string, unknown>;

    switch (modelTypeId) {
        case ModelType.NeuralNetwork:
        settings = build({
            LearningRate: 0.001,
            BatchSize: 10,
            Epochs: 5,
            Steps: 0,
            EmbedDim: needsEmbedding(schemaItems) ? 16 : 1,
            Optimizer: "Adam",
            Loss: "BinaryCrossentropy",
            Metrics: getDefaultMetrics(schemaItems),
            Sequential: getDefaultSequential(schemaItems),
            BatchOrdering: getFeatureOrdering(schemaItems),
        });
        break;

        case ModelType.KMeans:
        settings = build({
            OptimizationTolerance: 1e-7,
            MaximumNumberOfIterations: 1000,
            NumberOfClusters: null,
            NormalizeFeatures: "None"
        });
        break;

        case ModelType.LbfgsLogisticRegression:
        settings = build({
            L2Regularization: 1,
            L1Regularization: 1,
            OptimizationTolerance: 1e-7,
            MaximumNumberOfIterations: Number.MAX_SAFE_INTEGER,
            HistorySize: 20,
            NormalizeFeatures: "None"
        });
        break;

        case ModelType.LbfgsMaximumEntropy:
        settings = build({
            L2Regularization: 1,
            L1Regularization: 1,
            OptimizationTolerance: 1e-7,
            MaximumNumberOfIterations: Number.MAX_SAFE_INTEGER,
            HistorySize: 20,
            NormalizeFeatures: "None"
        });
        break;

        case ModelType.LbfgsPoissonRegression:
        settings = build({
            L2Regularization: 1,
            L1Regularization: 1,
            OptimizationTolerance: 1e-7,
            MaximumNumberOfIterations: Number.MAX_SAFE_INTEGER,
            HistorySize: 20,
            NormalizeFeatures: "None"
        });
        break;

        case ModelType.LinearSvm:
        settings = build({
            Lambda: 0.001,
            BatchSize: 1,
            PerformProjection: false,
            NoBias: false,
            NumberOfIterations: 1,
            InitialWeightsDiameter: 0,
            NormalizeFeatures: "None"
        });
        break;

        case ModelType.NaiveBayes:
        settings = build({
            NormalizeFeatures: "None"
        });
        break;

        case ModelType.OneVersusAll:
        settings = build({
            ModelKey: "AveragedPerceptron",
            NormalizeFeatures: "None"
        });
        break;

        case ModelType.PairwiseCoupling:
        settings = build({
            ModelKey: "AveragedPerceptron",
            NormalizeFeatures: "None"
        });
        break;

        case ModelType.OnlineGradientDescent:
        settings = build({
            LearningRate: 0.1,
            NumberOfIterations: 10,
            DecreaseLearningRate: true,
            LazyUpdate: true,
            ResetWeightsAfterXExamples: null,
            L2Regularization: 1e-7,
            RecencyGain: 0,
            RecencyGainMultiplicative: false,
            Averaged: true,
            NormalizeFeatures: "None"
        });
        break;

        case ModelType.AveragedPerceptron:
        settings = build({
            LearningRate: 1.0,
            NumberOfIterations: 10,
            DecreaseLearningRate: false,
            LazyUpdate: true,
            ResetWeightsAfterXExamples: null,
            L2Regularization: 1e-7,
            RecencyGain: 0,
            RecencyGainMultiplicative: false,
            Averaged: true,
            NormalizeFeatures: "None"
        });
        break;

        case ModelType.SdcaMaximumEntropy:
        settings = build({
            L2Regularization: 1e-6,
            ConvergenceTolerance: 1e-4,
            L1Regularization: 0.25,
            BiasLearningRate: 1,
            ConvergenceCheckFrequency: null,
            MaximumNumberOfIterations: 10,
            NormalizeFeatures: "None"
        });
        break;

        case ModelType.SdcaNonCalibrated:
        settings = build({
            L2Regularization: null,
            L1Regularization: null,
            ConvergenceTolerance: 0.1,
            MaximumNumberOfIterations: null,
            BiasLearningRate: 0,
            ConvergenceCheckFrequency: null,
            NormalizeFeatures: "None"
        });
        break;

        case ModelType.SdcaRegression:
        settings = build({
            L2Regularization: 1e-6,
            ConvergenceTolerance: 1e-4,
            L1Regularization: 0.25,
            BiasLearningRate: 1,
            ConvergenceCheckFrequency: null,
            MaximumNumberOfIterations: null,
            NormalizeFeatures: "None"
        });
        break;

        case ModelType.SgdNonCalibrated:
        settings = build({
            L2Regularization: 1e-6,
            ConvergenceTolerance: 1e-4,
            NumberOfIterations: 20,
            LearningRate: 0.01,
            PositiveInstanceWeight: 1,
            CheckFrequency: null,
            NormalizeFeatures: "None"
        });
        break;

      case ModelType.LightGbm:
        settings = build({
          NumberOfLeaves: 20,
          MinimumExampleCountPerLeaf: 10,
          UnbalancedSets: false,
          Sigmoid: 0.5,
          NumberOfIterations: 100,
          LearningRate: 0.5,
          NormalizeFeatures: "None"
        });
        break;

      case ModelType.RandomForest:
        settings = build({
          NumberOfTrees: 100,
          NumberOfLeaves: 20,
          MinimumExampleCountPerLeaf: 10,
          NormalizeFeatures: "None"
        });
        break;

      case ModelType.LightGbmRegression:
        settings = build({
          NumberOfLeaves: 20,
          MinimumExampleCountPerLeaf: 10,
          NumberOfIterations: 100,
          LearningRate: 0.5,
          NormalizeFeatures: "None"
        });
        break;

      case ModelType.RandomForestRegression:
        settings = build({
          NumberOfTrees: 100,
          NumberOfLeaves: 20,
          MinimumExampleCountPerLeaf: 10,
          NormalizeFeatures: "None"
        });
        break;

        case ModelType.Onnx:
        settings = build({});
        break;

        default:
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const _exhaustive: never = modelTypeId;
            throw new Error(`Unhandled model type: ${modelTypeId}`);
    }

  return JSON.stringify(settings, null, 2);
}
